import { useEffect, useState } from 'react';
import Modal from '../../ui/Modal/Modal';
import Input from '../../ui/Input/Input';
import Button from '../../ui/Button/Button';
import { paths } from '../../../routes/paths';
import styles from './LinkModal.module.css';

// Brutalist link editor. Two modes:
//   - URL    : free-form external href
//   - CHAPTER: pick a chapter from the current codex; emits an internal URL
//              `/workspaces/<wsId>/codices/<codexId>?chapter=<chapterId>`.
// Returns the resolved href via `onSubmit(href)`. `onRemove()` clears the mark.
export default function LinkModal({
  isOpen,
  onClose,
  onSubmit,
  onRemove,
  initialHref = '',
  workspaceId,
  codexId,
  chapters = [],
  currentChapterId,
}) {
  const initialMode = looksInternal(initialHref) ? 'chapter' : 'url';
  const [mode, setMode] = useState(initialMode);
  const [url, setUrl] = useState(initialHref);
  const [pickedId, setPickedId] = useState(
    initialMode === 'chapter' ? extractChapterId(initialHref) : null,
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const m = looksInternal(initialHref) ? 'chapter' : 'url';
    setMode(m);
    setUrl(initialHref);
    setPickedId(m === 'chapter' ? extractChapterId(initialHref) : null);
    setError(null);
  }, [isOpen, initialHref]);

  const picks = chapters.filter((c) => c.id !== currentChapterId);

  const handleSubmit = () => {
    if (mode === 'url') {
      const v = (url || '').trim();
      if (!v) {
        setError('URL is required');
        return;
      }
      onSubmit(v);
      return;
    }
    if (!pickedId) {
      setError('Pick a chapter');
      return;
    }
    const href = `${paths.codex(workspaceId, codexId)}?chapter=${pickedId}`;
    onSubmit(href);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialHref ? 'Edit link' : 'Insert link'}
      footer={
        <>
          {initialHref && onRemove && (
            <div className={styles.removeRow} style={{ marginRight: 'auto' }}>
              <Button variant="ghost" onClick={onRemove}>
                Remove link
              </Button>
            </div>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>{initialHref ? 'Update' : 'Insert'}</Button>
        </>
      }
    >
      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${mode === 'url' ? styles.tabActive : ''}`}
          onClick={() => {
            setMode('url');
            setError(null);
          }}
        >
          URL
        </button>
        <button
          type="button"
          className={`${styles.tab} ${mode === 'chapter' ? styles.tabActive : ''}`}
          onClick={() => {
            setMode('chapter');
            setError(null);
          }}
        >
          Chapter
        </button>
      </div>

      {mode === 'url' && (
        <Input
          label="URL"
          placeholder="https://example.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          error={error}
          autoFocus
        />
      )}

      {mode === 'chapter' && (
        <>
          {picks.length === 0 ? (
            <p className={styles.empty}>No other chapters in this codex.</p>
          ) : (
            <ul className={styles.list}>
              {picks.map((c) => (
                <li
                  key={c.id}
                  className={`${styles.row} ${pickedId === c.id ? styles.rowActive : ''}`}
                  onClick={() => setPickedId(c.id)}
                >
                  <span>{c.title || '(untitled)'}</span>
                  <span className={styles.rowMeta}>{c.id.slice(0, 8)}</span>
                </li>
              ))}
            </ul>
          )}
          {error && <p className={styles.empty}>{error}</p>}
        </>
      )}
    </Modal>
  );
}

function looksInternal(href) {
  return typeof href === 'string' && href.startsWith('/workspaces/') && href.includes('?chapter=');
}

function extractChapterId(href) {
  const m = /[?&]chapter=([0-9a-f-]{36})/i.exec(href || '');
  return m ? m[1] : null;
}
